import React from 'react';

import { useTenantContext } from './TenantProvider';
import { useAuthContext } from './AuthProvider';

/**
 * What the key says when nobody is signed in. The anonymous visitor is an ACCOUNT here,
 * not the absence of one: `/auth/accept-invite` publishes a `currentUser` while the page
 * stays mounted, so the state the anonymous branch built has to end somewhere.
 */
export const ANONYMOUS_ACCOUNT = 'anonymous';

/**
 * WHICH ACCOUNT the session-scoped providers are currently holding state for (PR-2.1).
 *
 * `AppSessionProviders` is remounted per route branch, which used to be the only account
 * boundary there was: `/auth ↔ /` was the only way to arrive as someone else. It no
 * longer is. The identity can now move with nothing unmounting — a cross-tab logout then
 * a login inside the same branch, a confirmed credential transition, the anonymous
 * accept-invite login, a super admin changing acting tenant. This key is what the
 * providers compare renders by; defined ONCE, because three providers that each derive
 * their own would disagree about when an account ended.
 *
 * Three parts, three ways the acting identity moves:
 *  - `currentUser.id` — a different person, anonymous included;
 *  - `tenant.id` — the same person acting in another tenant (super admin only, and the
 *    Firebase identity does not move with it);
 *  - `sessionRevision` — the same person signing in again explicitly. The auth owner
 *    counts these because `(sub, auth_time)` has second precision and cannot tell a
 *    re-sign-in of the same account apart from the sign-in it replaced.
 *
 * Deliberately NOT part of it: the identity of the `currentUser` object. Every
 * confirmation refetches `/auth/me` and hands back a fresh one for the same account;
 * reacting to that would drop the selected organization several times per session.
 *
 * `undefined` UNTIL THE SESSION HAS SETTLED. Both halves arrive late and independently on
 * a cold boot — the tenant from a GET, the account from the auth restore — and neither
 * provider blocks its children while it waits. Answering during that window would name
 * three different accounts before the user has done anything (`anonymous::0`,
 * `anonymous:<tenant>:0`, `<uid>:<tenant>:0`) and make every consumer tear its state down
 * twice on the way in. A later dip back into loading (a tenant refetch) keeps the last
 * settled answer, because nothing about the identity has been said to have changed.
 */
export const useAccountScopeKey = () => {
  const {
    isLoading: isSessionRestoring,
    sessionRevision,
    currentUser,
  } = useAuthContext();
  const { isLoading: isTenantLoading, tenant } = useTenantContext();

  const settledKeyRef = React.useRef<string | undefined>(undefined);

  if (isSessionRestoring || isTenantLoading) return settledKeyRef.current;

  settledKeyRef.current = `${currentUser?.id ?? ANONYMOUS_ACCOUNT}:${tenant?.id ?? ''}:${sessionRevision}`;
  return settledKeyRef.current;
};

/**
 * Runs `reset` at the moment the acting account changes, DURING the render that first
 * sees the new one.
 *
 * Not an effect. An effect lands one commit too late, and that commit is exactly the
 * dangerous one: it pairs the new `currentUser` with the previous account's selected
 * organization, upload queue and navigation state, and everything downstream — a
 * membership lookup, a branding read, a queued upload's follow-up mutation — is computed
 * from the pair before the reset arrives. Updating state during the render of the SAME
 * component is the supported way to say "this state belongs to the old props": React
 * throws the in-flight render away and re-runs it with the reset applied, without
 * rendering a single child from it.
 *
 * `reset` must therefore stay pure-ish: state setters of the calling provider and ref
 * writes only. No query invalidation, no worker teardown, no storage writes — those
 * belong in an effect keyed on `accountEpoch`, and a discarded render must not be able to
 * trigger them.
 *
 * Returns both halves of the answer:
 *  - `accountKey` — WHO, once known; `undefined` while the session is still settling.
 *  - `accountEpoch` — HOW MANY boundaries this mount has crossed. It starts at 0 and
 *    advances only between two SETTLED accounts, so adopting the first one a cold boot
 *    produces is not a boundary. Anything whose lifetime is one account — the upload
 *    worker, the cache clearing — hangs off this and not off the key, which necessarily
 *    moves once on the way in.
 */
export const useAccountScopeReset = (reset: (accountKey: string) => void) => {
  const accountKey = useAccountScopeKey();
  const [held, setHeld] = React.useState<{
    accountKey: string | undefined;
    accountEpoch: number;
  }>(() => ({ accountEpoch: 0, accountKey }));

  if (accountKey !== undefined && held.accountKey !== accountKey) {
    const isBoundary = held.accountKey !== undefined;
    setHeld({
      accountEpoch: isBoundary ? held.accountEpoch + 1 : held.accountEpoch,
      accountKey,
    });
    if (isBoundary) reset(accountKey);
  }

  return held;
};
